<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import InputText from 'primevue/inputtext';
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import axios from "axios";
import Swal from "sweetalert2";
import Dropdown from 'primevue/dropdown';
import InputSwitch from 'primevue/inputswitch';
import FileUpload from 'primevue/fileupload';
import Button from 'primevue/button';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: "question-edit",
  inject: {
    courseCtx: { default: null },
  },
  data() {
    return {
      v$: useVuelidate(),
      questionId: this.$route.params.id,
      gradeSlug: this.$route.params.gradeSlug,
      courseId: this.$route.params.courseId ?? null,
      title: "",
      description: "",
      fileData: null,
      previewUrl: null, // shows the saved image on load, then the newly selected/pasted one
      hadImage: false,  // whether the question already had a saved image on load
      is_file: true,
      formSending: false,
      lesson: null,
      lessons: [],
      unit: null,
      units: [],
      branch: null,
      branches: [],
      degree: null,
      type: 'mcq',
      typeOptions: [
        { label: 'اختيار من متعدد', value: 'mcq' },
        { label: 'إجابة قصيرة', value: 'short_answer' }
      ],
      difficulty_level: null,
      difficultyOptions: [
        { label: 'سهل', value: 'easy' },
        { label: 'متوسط', value: 'medium' },
        { label: 'صعب', value: 'hard' }
      ],
      answers: [],
    };
  },
  components: {
    Breadcrumb,
    InputText,
    Dropdown,
    InputSwitch,
    FileUpload,
    Button
  },
  validations() {
    return {
      // Title is OPTIONAL now — valid with text OR image (or both). The text-or-image
      // rule is enforced in sendForm() and again on the backend.
      degree: { required },
    };
  },
  computed: {
    isCourseScope() { return !!this.courseId; },
    basePath() {
      return this.isCourseScope ? `/courses/${this.courseId}/question-bank` : `/${this.gradeSlug}/questions`;
    },
    breadcrumbs() {
      if (this.isCourseScope) {
        const base = this.courseCtx?.breadcrumbBase ?? [];
        return [...base, { name: "بنك الأسئلة", path: { path: this.basePath, query: this.$route.query } }, { name: "تعديل سؤال", path: `${this.basePath}/edit/${this.questionId}` }];
      }
      return [
        { name: 'الرئيسية', path: '/' },
        { name: "سؤال", path: { path: this.basePath, query: this.$route.query } },
        { name: "تعديل سؤال", path: `/${this.gradeSlug}/question/edit/${this.questionId}` },
      ];
    },
    gradeStore() { return useGradeStore(); },
    resolvedGrade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          || this.gradeStore.allGrades.find(g => g.slug === slug)
          || null;
    },
    gradeId() { return this.resolvedGrade?.id ?? null; },
    isMCQ() {
      return this.type === 'mcq';
    }
  },
  methods: {
    fetchQuestion() {
      axios.get(`/questions/${this.questionId}`)
          .then(res => {
            const data = res.data.data;
            this.title = data.title;
            this.description = data.description;
            this.type = data.type;
            this.difficulty_level = data.difficulty_level;
            this.is_file = data.is_file ?? true;
            this.degree = data.degree;
            this.fileData = null;
            // Show the currently saved image (full asset URL from the API) as the
            // initial preview; a newly selected/pasted image will replace it.
            this.previewUrl = data.image || null;
            this.hadImage = !!data.image;
            this.answers = data.answers.map(a => ({
              id: a.id,               // keep the real row id so the backend updates in place
              title: a.title,
              image: null,
              correct: !!a.correct,
              question_id: a.question_id
            }));


            // fetch lesson hierarchy
            this.fetchBranch().then(() => {
              this.branch = this.branches.find(b => b.id === data.lesson?.parent?.parent?.id) || null;
              if (this.branch) {
                this.fetchUnits().then(() => {
                  this.unit = this.units.find(u => u.id === data.lesson?.parent?.id) || null;
                  if (this.unit) {
                    this.fetchLessons().then(() => {
                      this.lesson = this.lessons.find(l => l.id === data.lesson?.id) || null;
                    });
                  }
                });
              }
            });
          });
    },
    onFileChange(event) {
      if (event && event.target && event.target.files && event.target.files[0]) {
        this.setImageFile(event.target.files[0]);
      }
    },
    // Single entry point for BOTH file-select and paste: validates it is an allowed
    // image (JPG/PNG/WEBP, max 10 MB), stores the File for submission, and builds an
    // object-URL preview that replaces the currently-saved image.
    setImageFile(file) {
      if (!file || !file.type || !file.type.startsWith('image/')) return;
      const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowed.includes(file.type)) {
        Swal.fire({ icon: 'warning', title: 'صيغة غير مدعومة', text: 'يُسمح فقط بصيغ JPG و PNG و WEBP' });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire({ icon: 'warning', title: 'حجم الصورة كبير', text: 'حجم الصورة يجب ألا يتجاوز 10 ميجابايت' });
        return;
      }
      // Only object URLs need revoking; the initial preview may be a remote asset URL.
      if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl);
      this.fileData = file;
      this.previewUrl = URL.createObjectURL(file);
    },
    clearImage() {
      if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
      this.fileData = null;
    },
    // Ctrl + V: pull the first image off the clipboard, wrap it in a File with a
    // generated name (extension derived from the MIME type), and reuse setImageFile
    // so a pasted image behaves exactly like a normally-selected one on submit.
    onPaste(event) {
      const items = event.clipboardData && event.clipboardData.items;
      if (!items) return;
      for (const item of items) {
        if (item.kind === 'file' && item.type && item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (!blob) continue;
          const extMap = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif' };
          const ext = extMap[blob.type] || (blob.type.split('/')[1] || 'png');
          const named = new File([blob], `pasted-${Date.now()}.${ext}`, { type: blob.type });
          this.setImageFile(named);
          event.preventDefault();
          break;
        }
      }
    },
    onAnswerImageChange(event, index) {
      if (event && event.target && event.target.files) {
        this.answers[index].image = event.target.files[0];
      }
    },
    addAnswer() {
      if (this.answers.length < 4) {
        this.answers.push({ title: '', image: null, correct: false, question_id: null });
      } else {
        Swal.fire({ icon: 'warning', title: 'Maximum answers reached', text: 'You can only add up to 4 answers.' });
      }
    },
    removeAnswer(idx) {
      if (this.answers.length > 2) {
        this.answers.splice(idx, 1);
      } else {
        Swal.fire({ icon: 'warning', title: 'لا يمكن الحذف', text: 'يجب أن يكون هناك إجابتين على الأقل.' });
      }
    },
    setCorrect(idx) {
      this.answers = this.answers.map((a, i) => ({
        ...a,
        correct: i === idx
      }));
    },
    sendForm() {
      this.v$.$validate();
      if (this.v$.$error) return;

      // A question must have at least text OR an image. previewUrl is truthy when a
      // saved image is kept OR a new one was selected; null once the teacher removed it.
      const hasText = !!(this.title && this.title.trim());
      const hasImage = !!this.previewUrl;
      if (!hasText && !hasImage) {
        Swal.fire({ icon: 'warning', title: 'بيانات ناقصة', text: 'يجب إدخال نص السؤال أو رفع صورة للسؤال على الأقل.' });
        return;
      }

      const formData = new FormData();
      formData.append('title', this.title);
      formData.append('description', this.description);
      formData.append('lesson_id', this.lesson?.id ?? this.unit?.id ?? this.branch?.id);
      formData.append('type', this.type);
      formData.append('difficulty_level', this.difficulty_level);
      formData.append('is_file', this.is_file ? 1 : 0);

      // Backend update() reads $request->file('image'); only send when the admin
      // actually picked/pasted a new image, so an untouched image is preserved.
      if (this.fileData) {
        formData.append('image', this.fileData);
      } else if (this.hadImage && !this.previewUrl) {
        // Teacher removed the existing image and did NOT pick a new one → tell the
        // backend to delete it (keeps text-only). Never sent when only text changed.
        formData.append('remove_image', 1);
      }

      if (this.isMCQ) {
        this.answers.forEach((answer, idx) => {
          // Send the id for existing answers so the backend updates that exact row
          // instead of creating a duplicate. Newly added answers have no id.
          if (answer.id) {
            formData.append(`answers[${idx}][id]`, answer.id);
          }
          formData.append(`answers[${idx}][title]`, answer.title);
          formData.append(`answers[${idx}][correct]`, answer.correct ? 1 : 0);
          if (answer.image) {
            formData.append(`answers[${idx}][image]`, answer.image);
          }
        });
      }

      this.formSending = true;
      axios.post(`/questions/${this.questionId}?_method=PUT`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
          .then(() => {
            Swal.fire({ icon: "success", title: "تم التعديل بنجاح" });
            // Preserve the question-bank filters (branch/unit/lesson) on return.
            this.$router.push({ path: this.basePath, query: this.$route.query });
          })
          .catch((error) => {
            Swal.fire({ icon: "error", title: error.response?.data?.message || "حدث خطأ" });
          })
          .finally(() => {
            this.formSending = false;
          });
    },
    async fetchBranch() {
      const params = this.isCourseScope
          ? { type: 1, course_id: this.courseId }
          : { type: 1, academic_year_id: this.gradeId };
      try {
        const response = await axios.get('/lessons', { params });
        this.branches = response.data.data;
      } catch {
        this.branches = [];
      }
    },
    async fetchUnits() {
      const branchId = this.branch?.id;
      const params = this.isCourseScope
          ? { type: 2, course_id: this.courseId, parent_id: branchId }
          : { type: 2, academic_year_id: this.gradeId, parent_id: branchId };
      try {
        const response = await axios.get('/lessons', { params });
        this.units = response.data.data;
      } catch {
        this.units = [];
      }
    },
    async fetchLessons() {
      const unitId = this.unit?.id;
      const params = this.isCourseScope
          ? { type: 3, course_id: this.courseId, parent_id: unitId }
          : { type: 3, academic_year_id: this.gradeId, parent_id: unitId };
      try {
        const response = await axios.get('/lessons', { params });
        this.lessons = response.data.data;
      } catch {
        this.lessons = [];
      }
    },
    customLabelBranch(option) { return option ? (option.description ? `${option.title} (${option.description})` : option.title) : ''; },
    customLabelUnit(option) { return option ? (option.description ? `${option.title} (${option.description})` : option.title) : ''; },
    customLabelLesson(option) { return option ? (option.description ? `${option.title} (${option.description})` : option.title) : ''; },
  },
  async mounted() {
    if (!this.isCourseScope) {
      if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
      if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    }
    this.fetchQuestion();
    // Page-level Ctrl+V so the user can paste without hunting for the exact focus.
    window.addEventListener('paste', this.onPaste);
  },
  beforeUnmount() {
    window.removeEventListener('paste', this.onPaste);
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl);
  }
}
</script>

<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">
    <form class="row" @submit.prevent="sendForm()">
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>الفروع</label>
          <Dropdown v-model="branch" :options="branches" showClear :optionLabel="customLabelBranch"
                    placeholder="اختر الفرع" @update:model-value="fetchUnits" />
        </div>
      </div>

      <!-- Unit -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>الوحدات</label>
          <Dropdown v-model="unit" :options="units" showClear :optionLabel="customLabelUnit"
                    placeholder="اختر الوحده" @update:model-value="fetchLessons" />
        </div>
      </div>

      <!-- Lesson -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>الدروس</label>
          <Dropdown v-model="lesson" :options="lessons" showClear :optionLabel="customLabelLesson"
                    placeholder="اختر الدرس" />
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label for="title">نص السؤال (اختياري)</label>
          <InputText id="title" v-model="title" placeholder="أدخل نص السؤال (اختياري)" aria-describedby="title-help" />
          <small class="text-muted">يمكنك كتابة السؤال أو رفع صورة أو استخدام الاثنين معًا.</small>
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>النوع</label>
          <Dropdown v-model="type" :options="typeOptions" disabled optionLabel="label" optionValue="value" />
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>مستوى الصعوبة</label>
          <Dropdown v-model="difficulty_level" :options="difficultyOptions" optionLabel="label" optionValue="value" />
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label for="degree">درجة السؤال</label>
          <InputNumber id="degree" v-model="degree" inputId="degree" :useGrouping="false" fluid />
          <span class="text-danger" v-if="v$.degree.$error">برجاء ادخال درجة السؤال</span>
        </div>
      </div>
      <div class="col-12 mb-4">
        <label class="mb-2 d-block">صورة السؤال (اختياري)</label>

        <!-- Preview at NATURAL aspect ratio (object-fit: contain) — never cropped/stretched. -->
        <div v-if="previewUrl" class="q-image-preview">
          <img :src="previewUrl" class="q-image-preview__img" alt="معاينة صورة السؤال" />
        </div>
        <div v-if="previewUrl" class="d-flex gap-2 mt-2">
          <label class="btn btn-sm btn-outline-primary mb-0">
            <i class="pi pi-camera"></i> تغيير الصورة
            <input type="file" class="d-none" @change="onFileChange" accept="image/*" />
          </label>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="clearImage">
            <i class="pi pi-times"></i> إزالة الصورة
          </button>
        </div>

        <!-- Empty state: drop/click/paste zone. -->
        <label v-else class="upload-zone upload-zone--image">
          <div class="upload-zone__icon"><i class="pi pi-image"></i></div>
          <p class="upload-zone__label">اسحب الصورة هنا أو اضغط لاختيار صورة أو الصق الصورة باستخدام Ctrl + V</p>
          <p class="upload-zone__hint">PNG, JPG, WEBP, GIF</p>
          <input type="file" @change="onFileChange" accept="image/*" />
        </label>

        <small class="text-muted d-block mt-2">يمكنك كتابة السؤال أو رفع صورة أو استخدام الاثنين معًا.</small>
      </div>


      <!-- Answers Section (Only for MCQ) -->
      <div v-if="isMCQ" class="col-12 mt-4">
        <h5 class="mb-3">الإجابات</h5>
        <div v-for="(answer, idx) in answers" :key="idx" class="border rounded p-3 mb-3 position-relative answer-card">
          <div class="row g-3 align-items-center">
            <div class="col-md-6">
              <label class="form-label">نص الإجابة {{ idx + 1 }}</label>
              <InputText v-model="answer.title" placeholder="أدخل نص الإجابة" class="w-100" />
            </div>
            <div class="col-md-3">
              <label class="form-label">صورة (اختياري)</label>
              <FileUpload mode="basic" accept="image/*" @select="(e) => onAnswerImageChange(e, idx)" chooseLabel="صورة" />
            </div>
            <div class="col-md-2 text-center">
              <label class="form-label d-block">صحيحة؟</label>
              <InputSwitch v-model="answer.correct" @update:model-value="setCorrect(idx)" />
            </div>
            <div class="col-md-1 text-end">
              <button class="btn btn-danger" type="button" @click="removeAnswer(idx)"
                      :disabled="answers.length <= 2"><i class="pi pi-trash"></i></button>
            </div>
          </div>
          <small v-if="answer.correct" class="text-success mt-1 d-block">
            <i class="pi pi-check-circle"></i> هذه الإجابة الصحيحة
          </small>
        </div>
        <button label="إضافة إجابة جديدة" type="button" class="btn btn-success" @click="addAnswer" :disabled="answers.length >= 4">
          <i class="pi pi-plus"></i>
        </button>
      </div>
      <div class="col-lg-4 col-md-6 col-12 my-2"></div>
      <div class="col-lg-4 col-md-6 col-12 my-2"></div>
      <div class="col-lg-4 col-md-6 col-12 my-2">
        <button type="submit" class="btn primaryButton w-100" :disabled="formSending">
          <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
          {{ formSending ? 'جاري التعديل...' : 'تعديل' }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
/* Copy styles from add.vue */
</style>